import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  ThinTextField,
  AddButton,
  ThinDropdown,
} from "../../../components/common/GeneralComponent";
import { toast } from "react-toastify";
import { fetchData, submitFormWithErrors } from "../../../utils/functions";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

const BeneficiaryDetails = ({
  grantId,
  onNext,
  isEditMode,
  showNextOnly,
  onStoreFormData,
  onValidationChange,
  initialData,
  onGrantUpdated,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [isCheckingCnic, setIsCheckingCnic] = useState(false);

  const formatCNIC = (value) => {
    const cleaned = value.replace(/\D/g, "");
    if (cleaned.length <= 5) {
      return cleaned;
    } else if (cleaned.length <= 12) {
      return `${cleaned.slice(0, 5)}-${cleaned.slice(5)}`;
    } else {
      return `${cleaned.slice(0, 5)}-${cleaned.slice(5, 12)}-${cleaned.slice(
        12,
        13
      )}`;
    }
  };

  const isValidCNIC = (value) => {
    if (!value) return false;
    const cleanCnic = String(value).replace(/[-\s]/g, "");
    return /^[0-9]{13}$/.test(cleanCnic);
  };

  const formatContactNumber = (value) => {
    const cleaned = value.replace(/\D/g, "");
    if (cleaned.length <= 4) {
      return cleaned;
    } else if (cleaned.length <= 11) {
      return `${cleaned.slice(0, 4)}-${cleaned.slice(4)}`;
    } else {
      return `${cleaned.slice(0, 4)}-${cleaned.slice(4, 11)}`;
    }
  };

  const isValidPhone = (value) => {
    if (!value) return false;
    const cleaned = String(value).replace(/[-\s]/g, "");
    return /^03[0-9]{9}$/.test(cleaned);
  };

  const validationSchema = Yup.object({
    application_id: Yup.string().required("Application ID is required"),
    company_name: Yup.string().required("Company Name is required"),
    applicant_full_name: Yup.string().required(
      "Applicant Full Name is required"
    ),
    father_husband_name: Yup.string(),
    cnic_number: Yup.string()
      .test(
        "cnic-format",
        "CNIC must be in format: 12345-1234567-1",
        function (value) {
          if (!value) return false;
          return isValidCNIC(value);
        }
      )
      .required("CNIC Number is required"),
    gender: Yup.string().required("Gender is required"),
    date_of_birth: Yup.date()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value
      )
      .nullable()
      .max(new Date(), "Date of Birth cannot be in the future"),
    marital_status: Yup.string(),
    contact_number: Yup.string()
      .test(
        "phone-format",
        "Contact number must be 11 digits starting with 03 (format: 03XX-XXXXXXX)",
        function (value) {
          if (!value) return false;
          return isValidPhone(value);
        }
      )
      .required("Contact Number is required"),

    // ✅ Improved Email Validation
    email: Yup.string()
      .email("Invalid email format")
      .matches(
        /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/,
        "Email must be a valid address with domain and TLD"
      )
      .required("Email is required"),
    address: Yup.string().required("Address is required"),
    person_with_disability: Yup.boolean().required(
      "Person with Disability is required"
    ),
  });

  const formik = useFormik({
    initialValues: {
      application_id: initialData?.application_id || "",
      company_name: initialData?.company_name || "",
      applicant_full_name: initialData?.applicant_full_name || "",
      father_husband_name: initialData?.father_husband_name || "",
      cnic_number: initialData?.cnic_number || "",
      gender: initialData?.gender || "",
      date_of_birth: initialData?.date_of_birth || "",
      marital_status: initialData?.marital_status || "",
      contact_number: initialData?.contact_number || "",
      email: initialData?.email || "",
      address: initialData?.address || "",
      person_with_disability: initialData?.person_with_disability ?? "",
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      await handleFormSubmit(values);
    },
  });

  const handleFormSubmit = async (values) => {
    setLoading(true);
    try {
      if (isEditMode) {
        const appIdValid = await validateApplicationIdInDatabase(values.application_id, grantId);
        if (!appIdValid) { setLoading(false); return; }

        if (values.cnic_number && isValidCNIC(values.cnic_number)) {
          const cnicCheck = await fetchData(
            `micro-grants/validate-cnic/${encodeURIComponent(values.cnic_number)}`,
            "GET", null, {}, { exclude_id: grantId }, navigate, "", dispatch
          );
          if (cnicCheck?.exists) {
            toast.error("A grant with this CNIC already exists");
            formik.setFieldError("cnic_number", "This CNIC is already registered");
            setLoading(false);
            return;
          }
        }

        const response = await fetchData(
          `micro-grants/${grantId}`,
          "PUT",
          values,
          {},
          {},
          navigate,
          "",
          dispatch
        );

        if (response) {
          toast.success("Beneficiary details updated successfully");
          if (onGrantUpdated) {
            onGrantUpdated();
          }
        }
      } else {
        if (onStoreFormData) {
          onStoreFormData(values);
        }

        toast.success("Beneficiary details saved locally");
        if (onNext) onNext();
      }
    } catch (error) {
      console.error("Error saving beneficiary details:", error);
      toast.error("Failed to save beneficiary details");
    } finally {
      setLoading(false);
    }
  };

  const validateApplicationIdInDatabase = async (applicationId, excludeId = null) => {
    if (!applicationId) return true;
    try {
      const response = await fetchData(
        `micro-grants/validate-application-id/${encodeURIComponent(applicationId)}`,
        "GET",
        null,
        {},
        excludeId ? { exclude_id: excludeId } : {},
        navigate,
        "",
        dispatch
      );
      if (response && response.exists) {
        toast.error("A grant with this Application ID already exists");
        formik.setFieldError("application_id", "This Application ID is already registered");
        return false;
      }
      return true;
    } catch (error) {
      console.error("Error validating Application ID:", error);
      return true;
    }
  };

  const handleNextClick = async () => {
    if (loading || isCheckingCnic) return;

    // Run Yup validation first to catch format errors before DB calls
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      formik.setTouched(
        Object.keys(formik.values).reduce((acc, key) => {
          acc[key] = true;
          return acc;
        }, {})
      );
      return;
    }

    // Check Application ID uniqueness
    if (formik.values.application_id) {
      const appIdValid = await validateApplicationIdInDatabase(formik.values.application_id);
      if (!appIdValid) return;
    }

    // Check CNIC uniqueness
    if (formik.values.cnic_number && isValidCNIC(formik.values.cnic_number)) {
      const cnicIsValid = await validateCnicInDatabase(formik.values.cnic_number);
      if (!cnicIsValid) return;
    }

    if (onStoreFormData) {
      onStoreFormData(formik.values);
    }

    toast.success("Beneficiary details saved");

    setTimeout(() => {
      if (onNext) {
        onNext();
      }
    }, 100);
  };

  const validateCnicInDatabase = async (cnicValue) => {
    if (!cnicValue || !isValidCNIC(cnicValue)) return;

    setIsCheckingCnic(true);
    try {
      const response = await fetchData(
        `micro-grants/validate-cnic/${encodeURIComponent(cnicValue)}`,
        "GET",
        null,
        {},
        {},
        navigate,
        "",
        dispatch
      );

      if (response && response.exists) {
        toast.error("A grant with this CNIC already exists");
        formik.setFieldError("cnic_number", "This CNIC is already registered");
        return false;
      }
      return true;
    } catch (error) {
      console.error("Error validating CNIC:", error);
      return true;
    } finally {
      setIsCheckingCnic(false);
    }
  };

  useEffect(() => {
    if (onValidationChange) {
      const timeoutId = setTimeout(() => {
        onValidationChange(formik.isValid);
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [formik.isValid, onValidationChange]);

  useEffect(() => {
    if (onStoreFormData && !isEditMode) {
      const timeoutId = setTimeout(() => {
        onStoreFormData(formik.values);
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [formik.values, onStoreFormData, isEditMode]);

  const MaritalStatusOptions = [
    { label: "Married", value: "Married" },
    { label: "Widow/ Widower", value: "Widow/ Widower" },
    { label: "Divorced", value: "Divorced" },
    { label: "Separated", value: "Separated" },
    { label: "Never Married", value: "Never Married" },
  ];

  const genderOptions = [
    { label: "Male", value: "Male" },
    { label: "Female", value: "Female" },
    { label: "Transgender", value: "Transgender" },
  ];

  const yesNoOptions = [
    { label: "Yes", value: "true" },
    { label: "No", value: "false" },
  ];

  return (
    <form onSubmit={submitFormWithErrors(formik)} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <ThinTextField
            label="Application ID"
            name="application_id"
            value={formik.values.application_id}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.application_id && formik.errors.application_id
            }
            placeholder="Enter Application ID"
          />
          {formik.touched.application_id && formik.errors.application_id && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.application_id}
            </div>
          )}
        </div>

        <div>
          <ThinTextField
            label="Company Name"
            name="company_name"
            value={formik.values.company_name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.company_name && formik.errors.company_name}
            placeholder="Enter Company Name"
          />
          {formik.touched.company_name && formik.errors.company_name && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.company_name}
            </div>
          )}
        </div>

        <div>
          <ThinTextField
            label="Full Name of Applicant/Grant Recipient"
            name="applicant_full_name"
            value={formik.values.applicant_full_name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.applicant_full_name &&
              formik.errors.applicant_full_name
            }
            placeholder="Enter Full Name"
          />
          {formik.touched.applicant_full_name &&
            formik.errors.applicant_full_name && (
              <div className="text-red-500 text-sm mt-1">
                {formik.errors.applicant_full_name}
              </div>
            )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="mt-1">
          <ThinTextField
            label="Father/Husband Name"
            name="father_husband_name"
            value={formik.values.father_husband_name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.father_husband_name &&
              formik.errors.father_husband_name
            }
            placeholder="Enter Father/Husband Name"
            isRequired={false}
          />
          {formik.touched.father_husband_name &&
            formik.errors.father_husband_name && (
              <div className="text-red-500 text-sm mt-1">
                {formik.errors.father_husband_name}
              </div>
            )}
        </div>

        <div className="mt-1">
          <ThinTextField
            label="CNIC Number"
            name="cnic_number"
            value={formik.values.cnic_number}
            onChange={(e) => {
              const cleanValue = e.target.value.replace(/\D/g, "");
              if (cleanValue.length <= 13) {
                const formatted = formatCNIC(cleanValue);
                formik.setFieldValue("cnic_number", formatted);

                if (formik.errors.cnic_number) {
                  formik.setFieldError("cnic_number", undefined);
                }
              }
            }}
            onBlur={async (e) => {
              formik.handleBlur(e);
              if (isValidCNIC(e.target.value)) {
                await validateCnicInDatabase(e.target.value);
              }
            }}
            error={formik.touched.cnic_number && formik.errors.cnic_number}
            placeholder="XXXXX-XXXXXXX-X"
            maxLength={15}
            disabled={isCheckingCnic}
          />
          {isCheckingCnic && (
            <div className="text-blue-500 text-sm mt-1">
              Checking CNIC availability...
            </div>
          )}
          {formik.touched.cnic_number && formik.errors.cnic_number && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.cnic_number}
            </div>
          )}
        </div>

        <div>
          <ThinDropdown
            label="Gender"
            id="gender-dropdown"
            options={genderOptions}
            value={formik.values.gender}
            onChange={formik.handleChange}
            name="gender"
            error={formik.touched.gender && formik.errors.gender}
          />
          {formik.touched.gender && formik.errors.gender && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.gender}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="mt-1">
          <ThinTextField
            label="Date of Birth"
            name="date_of_birth"
            type="date"
            value={formik.values.date_of_birth}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.date_of_birth && formik.errors.date_of_birth}
            max={new Date().toISOString().split("T")[0]}
            isRequired={false}
          />
          {formik.touched.date_of_birth && formik.errors.date_of_birth && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.date_of_birth}
            </div>
          )}
        </div>

        <div>
          <ThinDropdown
            label="Person with Disability"
            id="person-with-disability-dropdown"
            options={yesNoOptions}
            value={
              formik.values.person_with_disability === ""
                ? ""
                : String(formik.values.person_with_disability)
            }
            onChange={(e) =>
              formik.setFieldValue(
                "person_with_disability",
                e.target.value === "" ? "" : e.target.value === "true"
              )
            }
            onBlur={formik.handleBlur}
            name="person_with_disability"
            error={
              formik.touched.person_with_disability &&
              formik.errors.person_with_disability
            }
          />
          {formik.touched.person_with_disability &&
            formik.errors.person_with_disability && (
              <div className="text-red-500 text-sm mt-1">
                {formik.errors.person_with_disability}
              </div>
            )}
        </div>

        <div>
          <ThinDropdown
            label="Marital Status"
            id="marital-status-dropdown"
            options={MaritalStatusOptions}
            value={formik.values.marital_status}
            onChange={formik.handleChange}
            name="marital_status"
            error={
              formik.touched.marital_status && formik.errors.marital_status
            }
            required={false}
          />
          {formik.touched.marital_status && formik.errors.marital_status && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.marital_status}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="mt-1">
          <ThinTextField
            label="Contact Number"
            name="contact_number"
            value={formik.values.contact_number}
            onChange={(e) => {
              const cleanValue = e.target.value.replace(/\D/g, "");
              if (
                cleanValue === "" ||
                cleanValue === "0" ||
                cleanValue.startsWith("03")
              ) {
                if (cleanValue.length <= 11) {
                  const formatted = formatContactNumber(cleanValue);
                  formik.setFieldValue("contact_number", formatted);
                }
              }
            }}
            onBlur={formik.handleBlur}
            error={
              formik.touched.contact_number && formik.errors.contact_number
            }
            placeholder="03XX-XXXXXXX"
            maxLength={12}
          />
          {formik.touched.contact_number && formik.errors.contact_number && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.contact_number}
            </div>
          )}
        </div>

        <div>
          <ThinTextField
            label="Email"
            name="email"
            type="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && formik.errors.email}
            placeholder="example@email.com"
          />
          {formik.touched.email && formik.errors.email && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.email}
            </div>
          )}
        </div>

        <div>
          <ThinTextField
            label="Address"
            name="address"
            value={formik.values.address}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.address && formik.errors.address}
            placeholder="Enter complete address"
          />
          {formik.touched.address && formik.errors.address && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.address}
            </div>
          )}
        </div>
      </div>

      <div className="pt-4 flex justify-end gap-3">
        {showNextOnly && !isEditMode && (
          <button
            type="button"
            onClick={handleNextClick}
            className="bg-teal-600 text-white rounded-lg px-4 py-2 hover:bg-teal-700 transition-colors disabled:opacity-50"
            disabled={loading || isCheckingCnic}
          >
            {loading ? "Saving..." : isCheckingCnic ? "Validating..." : "Next"}
          </button>
        )}
        {isEditMode && (
          <AddButton
            text="Update"
            type="submit"
            isLoading={loading}
            disabled={loading}
          />
        )}
      </div>
    </form>
  );
};

export default BeneficiaryDetails;
